/**
 * Card "Avaliação Física" — últimas 3 avaliações + botão "+ Nova".
 * Botão abre o AssessmentForm (modal) que já existe no app.
 *
 * Props:
 *   - student: aluno (passado pro AssessmentForm — ele lê dados base
 *     pra pré-preencher campos como nome, sexo etc)
 *
 * Estado interno:
 *   - `modalVisible`: controla o AssessmentForm
 *   - usa `useProgress(studentId)` pra listar `records` e expor
 *     `addRecordForStudent` (chamado quando o modal salva)
 *
 * Decisões:
 *   - Mostra só 3 últimas avaliações. Link "Ver histórico completo →"
 *     leva pra tela de progresso (/progress/[id]).
 *   - `records.slice(-3).reverse()` — pega as 3 últimas em ordem
 *     descendente (mais recente primeiro).
 */

import { memo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { Student } from '@/lib/types';
import { useProgress } from '@/hooks/useProgress';
import AssessmentForm, { AssessmentData } from '@/components/AssessmentForm';
import { sharedStyles } from './styles';

interface Props {
  student: Student;
}

function AssessmentSectionImpl({ student }: Props) {
  const router = useRouter();
  const { records, addRecordForStudent } = useProgress(student.id);
  const [modalVisible, setModalVisible] = useState(false);

  async function handleSave(data: AssessmentData) {
    await addRecordForStudent({ studentId: student.id, ...data });
  }

  return (
    <View style={sharedStyles.section}>
      <View style={sharedStyles.sectionRow}>
        <Text style={sharedStyles.sectionTitle}>Avaliação Física</Text>
        <TouchableOpacity onPress={() => setModalVisible(true)}>
          <Text style={sharedStyles.linkText}>+ Nova Avaliação</Text>
        </TouchableOpacity>
      </View>

      {records.length === 0 ? (
        <Text style={sharedStyles.empty}>Nenhuma avaliação registrada</Text>
      ) : (
        records.slice(-3).reverse().map(r => (
          <View key={r.id} style={styles.row}>
            <Text style={styles.date}>
              {new Date(r.recorded_at).toLocaleDateString('pt-BR')}
            </Text>
            <Text style={styles.data}>
              {r.weight_kg} kg
              {r.body_fat_pct != null ? ` · ${r.body_fat_pct.toFixed(1)}% gord.` : ''}
              {r.measurements?.waist ? ` · ${r.measurements.waist}cm cintura` : ''}
            </Text>
          </View>
        ))
      )}

      {records.length > 3 && (
        <TouchableOpacity onPress={() => router.push(`/(personal)/progress/${student.id}` as any)}>
          <Text style={[sharedStyles.linkText, { marginTop: 6 }]}>Ver histórico completo →</Text>
        </TouchableOpacity>
      )}

      <AssessmentForm
        visible={modalVisible}
        student={student}
        onSave={handleSave}
        onClose={() => setModalVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: '#eee',
  },
  date: { fontSize: 11, color: '#888', marginBottom: 2 },
  data: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
});

export const AssessmentSection = memo(AssessmentSectionImpl);
